import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { type TemplateSettings, type DocumentType, DEFAULT_TEMPLATE_SETTINGS } from './types';
import { documentsApi } from '@/lib/api';

// Template metadata from API
interface TemplateInfo {
  slug: string;
  name: string;
  category: string;
  description: string;
  previewSvg?: string;
}

interface TemplateDesignerContextType {
  settings: TemplateSettings;
  updateSettings: <K extends keyof TemplateSettings>(
    key: K,
    value: TemplateSettings[K]
  ) => void;
  updateNestedSettings: <K extends keyof TemplateSettings>(
    key: K,
    updates: Partial<TemplateSettings[K]>
  ) => void;
  resetSettings: () => void;
  loadSettings: (settings: TemplateSettings) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  previewDocumentType: DocumentType;
  setPreviewDocumentType: (type: DocumentType) => void;
  activeSection: string;
  setActiveSection: (section: string) => void;
  isPreviewAnimating: boolean;
  triggerPreviewAnimation: () => void;
  // Template list from API
  availableTemplates: string[];
  templates: TemplateInfo[];
  templatesLoaded: boolean;
  templatesError: string | null;
}

const TemplateDesignerContext = createContext<TemplateDesignerContextType | null>(null);

interface TemplateDesignerProviderProps {
  children: ReactNode;
  initialSettings?: Partial<TemplateSettings>;
}

export function TemplateDesignerProvider({ children, initialSettings }: TemplateDesignerProviderProps) {
  const [settings, setSettings] = useState<TemplateSettings>(() => ({
    ...DEFAULT_TEMPLATE_SETTINGS,
    ...initialSettings,
  }));
  const [isLoading, setIsLoading] = useState(true);
  const [previewDocumentType, setPreviewDocumentType] = useState<DocumentType>('invoice');
  const [activeSection, setActiveSection] = useState('logo');
  const [isPreviewAnimating, setIsPreviewAnimating] = useState(false);
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  // Derived: just the slugs for quick lookup
  const availableTemplates = templates.map(t => t.slug);

  // Fetch templates on mount
  useEffect(() => {
    let mounted = true;

    async function fetchTemplates() {
      try {
        setTemplatesError(null);

        // Fetch templates list (document type only, not receipts)
        const response = await documentsApi.listTemplates('document');

        if (mounted && response.data?.templates) {
          const templateList: TemplateInfo[] = response.data.templates.map((t: {
            slug: string;
            metadata?: { name?: string; category?: string; description?: string };
            preview_svg?: string;
          }) => ({
            slug: t.slug,
            name: t.metadata?.name || t.slug,
            category: t.metadata?.category || 'modern',
            description: t.metadata?.description || '',
            previewSvg: t.preview_svg,
          }));

          setTemplates(templateList);
          setTemplatesLoaded(true);

          // Auto-select first template if current doesn't exist
          const templateSlugs = templateList.map(t => t.slug);
          setSettings(prev => {
            const currentExists = templateSlugs.includes(prev.selectedLayout);
            if (!currentExists && templateSlugs.length > 0) {
              return { ...prev, selectedLayout: templateSlugs[0] };
            }
            return prev;
          });
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
        if (mounted) {
          setTemplatesError('Failed to load templates');
          setTemplatesLoaded(true); // Still mark loaded so UI doesn't hang
        }
      }
    }

    fetchTemplates();

    return () => {
      mounted = false;
    };
  }, []);

  const loadSettings = useCallback((newSettings: TemplateSettings) => {
    setSettings(newSettings);
  }, []);

  const updateSettings = useCallback(<K extends keyof TemplateSettings>(
    key: K,
    value: TemplateSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    triggerPreviewAnimation();
  }, []);

  const updateNestedSettings = useCallback(<K extends keyof TemplateSettings>(
    key: K,
    updates: Partial<TemplateSettings[K]>
  ) => {
    setSettings(prev => ({
      ...prev,
      [key]: { ...prev[key] as object, ...updates },
    }));
    triggerPreviewAnimation();
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_TEMPLATE_SETTINGS);
  }, []);

  const triggerPreviewAnimation = useCallback(() => {
    setIsPreviewAnimating(true);
    setTimeout(() => setIsPreviewAnimating(false), 300);
  }, []);

  return (
    <TemplateDesignerContext.Provider
      value={{
        settings,
        updateSettings,
        updateNestedSettings,
        resetSettings,
        loadSettings,
        isLoading,
        setIsLoading,
        previewDocumentType,
        setPreviewDocumentType,
        activeSection,
        setActiveSection,
        isPreviewAnimating,
        triggerPreviewAnimation,
        availableTemplates,
        templates,
        templatesLoaded,
        templatesError,
      }}
    >
      {children}
    </TemplateDesignerContext.Provider>
  );
}

export function useTemplateDesigner() {
  const context = useContext(TemplateDesignerContext);
  if (!context) {
    throw new Error('useTemplateDesigner must be used within a TemplateDesignerProvider');
  }
  return context;
}
